import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {useGuideLanguages} from "@khan/guide-core";
import {labelForGuideLanguageSelect} from "@khan/guide-utils";

interface Props {
    style?: StyleType;
    currentLanguage: string | null;
    onChange: (value: string) => void;
    isLoading?: boolean;
}

/**
 * A component to allow users to select the language they'd like AI Guide to
 * respond with.
 */
const GuideLanguageSetting = ({
    style,
    currentLanguage,
    onChange,
    isLoading,
}: Props): React.ReactElement => {
    const guideLanguagesResult = useGuideLanguages();

    const isLoadingLanguages = guideLanguagesResult.status === "loading";

    const defaultLanguageCode =
        guideLanguagesResult.status === "success"
            ? guideLanguagesResult.data.defaultLanguageCode
            : "en";

    // We want to order the options such that the Default language is always
    // at the top of the list.
    const sortedGuideLanguages =
        guideLanguagesResult.status === "success"
            ? guideLanguagesResult.data.guideLanguages.sort((a, b) => {
                  if (
                      defaultLanguageCode === b.id ||
                      defaultLanguageCode === a.id
                  ) {
                      return defaultLanguageCode === b.id ? 1 : -1;
                  }
                  return 0;
              })
            : [];

    return (
        <View style={style}>
            <LabelLarge tag="label" htmlFor="select-guide-language">
                {t`Language`}
            </LabelLarge>
            <LabelSmall style={styles.subtitle}>
                {t`Choose the language you'd like Khanmigo to respond to you with.`}
            </LabelSmall>
            <Strut size={spacing.medium_16} />
            {isLoadingLanguages ? (
                <CircularSpinner size="small" style={styles.spinner} />
            ) : (
                <SingleSelect
                    id="select-guide-language"
                    style={styles.select}
                    placeholder={
                        isLoading ? t`Loading...` : t`Select a language`
                    }
                    selectedValue={currentLanguage || undefined}
                    onChange={onChange}
                >
                    {sortedGuideLanguages.map(({id, localName}) => (
                        <OptionItem
                            key={id}
                            label={labelForGuideLanguageSelect(
                                id!,
                                localName!,
                                id === defaultLanguageCode,
                            )}
                            value={id!}
                        />
                    ))}
                </SingleSelect>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    select: {
        width: "100%",
    },
    subtitle: {
        color: semanticColor.core.foreground.neutral.default,
    },
    spinner: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        // Approximately match the height of the select plus the label.
        height: 40,
    },
});

export default React.memo(GuideLanguageSetting);
